import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

export default function TesteAtivacaoParceiros() {
  const [parceiroId, setParceiroId] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const { toast } = useToast();

  const handleAtivar = async () => {
    if (!parceiroId.trim()) {
      toast({
        title: "⚠️ ID obrigatório",
        description: "Digite o ID do parceiro para ativar.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    setResultado(null);

    try {
      const response = await fetch('/api/parceiros-auth/ativar-teste', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parceiro_id: parceiroId.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResultado({
          sucesso: true,
          data: data
        });
        
        toast({
          title: "🎉 Parceiro ativado!",
          description: data.message,
        });

        // Limpar campo
        setParceiroId('');
      } else {
        setResultado({
          sucesso: false,
          erro: data.error
        });
        
        toast({
          title: "❌ Erro ao ativar",
          description: data.error,
          variant: "destructive"
        });
      }
    } catch (error: any) {
      setResultado({
        sucesso: false,
        erro: error.message
      });
      
      toast({
        title: "❌ Erro de conexão",
        description: "Não foi possível conectar ao servidor.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🧪 Teste de Ativação de Parceiros
          </CardTitle>
          <CardDescription>
            Ferramenta de desenvolvimento para ativar parceiros manualmente
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Aviso de Desenvolvimento */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-yellow-600 text-xl">⚠️</div>
              <div>
                <h4 className="font-semibold text-yellow-800">Modo Desenvolvimento</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Esta ferramenta só funciona em ambiente de desenvolvimento. 
                  Em produção, use o fluxo normal de e-mail.
                </p>
              </div>
            </div>
          </div>

          {/* Formulário */}
          <div className="space-y-4">
            <div>
              <label htmlFor="parceiroId" className="block text-sm font-medium mb-2">
                ID do Parceiro *
              </label>
              <Input
                id="parceiroId"
                type="text"
                value={parceiroId}
                onChange={(e) => setParceiroId(e.target.value)}
                placeholder="Cole o ID do parceiro aqui"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Você pode encontrar o ID na resposta da API ao cadastrar um parceiro
              </p>
            </div>

            <Button 
              onClick={handleAtivar}
              disabled={loading || !parceiroId.trim()}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ativando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Ativar Parceiro
                </>
              )}
            </Button>
          </div>

          {/* Resultado */}
          {resultado && (
            <div className={`border rounded-lg p-4 ${
              resultado.sucesso 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {resultado.sucesso ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className={`font-semibold ${
                    resultado.sucesso ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {resultado.sucesso ? 'Sucesso!' : 'Erro'}
                  </h4>
                  <div className="mt-2 text-sm">
                    {resultado.sucesso ? (
                      <div className="space-y-2">
                        <p className="text-green-700">
                          <strong>Parceiro:</strong> {resultado.data.parceiro.nome}
                        </p>
                        <p className="text-green-700">
                          <strong>E-mail:</strong> {resultado.data.parceiro.email}
                        </p>
                        <p className="text-green-700">
                          <strong>Status:</strong> Ativado com sucesso
                        </p>
                        <div className="bg-yellow-100 border border-yellow-300 rounded p-2 mt-3">
                          <p className="text-yellow-800 text-xs">
                            ⚠️ {resultado.data.aviso}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-red-700">
                        <strong>Erro:</strong> {resultado.erro}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instruções */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">Como usar:</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Cadastre um novo parceiro normalmente</li>
              <li>Copie o ID do parceiro da resposta da API</li>
              <li>Cole o ID no campo acima e clique em "Ativar Parceiro"</li>
              <li>O parceiro agora pode fazer login normalmente</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
